import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkle, ArrowRight, Envelope, Check } from "@phosphor-icons/react";
import { useI18n } from "../lib/i18n";
import { forgotPassword, formatApiError } from "../lib/api";

export default function ForgotPasswordPage() {
  const { lang } = useI18n();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await forgotPassword(email.trim(), lang);
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-6 py-12" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 kicker-label mb-4">
            <Sparkle size={14} weight="duotone" className="text-forest" />
            {lang === "el" ? "Επαναφορά κωδικού" : "Password reset"}
          </div>
          <h1 className="font-heading font-semibold text-[36px] md:text-[44px] leading-[0.95] tracking-tight text-ink">
            {sent
              ? (lang === "el" ? "Έλεγξε τα email σου." : "Check your email.")
              : (lang === "el" ? "Ξέχασες τον κωδικό σου;" : "Forgot your password?")}
          </h1>
          <p className="mt-3 text-ink-soft text-sm">
            {sent
              ? (lang === "el"
                  ? "Αν υπάρχει λογαριασμός με αυτό το email, θα λάβεις link επαναφοράς στα επόμενα λεπτά."
                  : "If an account with that email exists, you'll receive a reset link in the next few minutes.")
              : (lang === "el"
                  ? "Γράψε το email σου και θα σου στείλουμε link για νέο κωδικό."
                  : "Enter your email and we'll send you a link to set a new password.")}
          </p>
        </div>

        {!sent ? (
          <form onSubmit={submit} className="space-y-4 bg-white border border-line rounded-2xl canvas-shadow p-6" data-testid="forgot-form">
            <div>
              <label className="kicker-label mb-2 block">Email</label>
              <div className="relative">
                <Envelope size={16} weight="duotone" className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-soft" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@example.com"
                  className="cs-input !pl-11"
                  data-testid="forgot-email"
                  autoFocus
                />
              </div>
            </div>

            {error && (
              <div className="text-xs text-destructive bg-destructive/5 border border-destructive/20 rounded-lg px-3 py-2" data-testid="forgot-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-forest text-cream rounded-full px-6 py-3.5 text-sm font-medium hover:bg-forest-dark transition disabled:opacity-60"
              data-testid="forgot-submit"
            >
              {loading ? "…" : (lang === "el" ? "Στείλε μου link" : "Send reset link")}
              <ArrowRight size={14} weight="bold" />
            </button>

            <div className="text-center text-sm text-ink-soft pt-2">
              <Link to="/login" className="text-forest hover:text-forest-dark font-medium" data-testid="back-to-login">
                ← {lang === "el" ? "Πίσω στη σύνδεση" : "Back to sign in"}
              </Link>
            </div>
          </form>
        ) : (
          <div className="bg-white border border-line rounded-2xl canvas-shadow p-8 text-center" data-testid="forgot-sent">
            <div className="w-12 h-12 rounded-full bg-forest/10 flex items-center justify-center mx-auto mb-4">
              <Check size={20} weight="bold" className="text-forest" />
            </div>
            <p className="text-sm text-ink-soft mb-6">
              {lang === "el"
                ? "Αν δεν λάβεις το email σε 5 λεπτά, έλεγξε τον φάκελο spam."
                : "If you don't see it in 5 minutes, check your spam folder."}
            </p>
            <Link to="/login" className="text-forest hover:text-forest-dark text-sm font-medium" data-testid="back-to-login">
              ← {lang === "el" ? "Πίσω στη σύνδεση" : "Back to sign in"}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
